import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ArrowLeft } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import CustomButton from '../../components/CustomButton';
import {
    makeBookingsSearchUrl,
    makeIssueReportDetailsUrl,
    makeOrganizationDetailUrl,
    paths,
} from '../../routes';
import { tenantSelector } from '../../selector/authenticationSelector';
import { useGetAccountIssueReportsQuery, useGetAccountQuery } from '../../api/customerAccount/customerAccountApiSlice';
import {
    AccountIssueReport,
    AccountSummary,
} from '../../api/customerAccount/customerAccountApiTypes';
import { formatDate } from '../../utils';

/**
 * One customer account (H-3a), reached from the Customer accounts tab.
 *
 * It deliberately shows no more than the list does today. The point is the
 * container: what we know about an account is going to grow — memberships,
 * last login, consent, payments — and a table row cannot hold that without
 * horizontal scrolling (PO, 2026-08-20). The place to put the next field now
 * exists.
 *
 * Read-only, like the list: accounts are created and edited by the end user in
 * the booking app, never by an operator.
 *
 * Restored from the URL param, never from a preselected list entry: the list
 * is paginated at 25, so an account past the first page is not in that cache,
 * and a deep link or refresh has no list at all (CLAUDE.md — a page gated on a
 * selected entity restores it from the URL).
 */

const Field: React.FC<{ label: string, children: React.ReactNode }> = ({ label, children }) => (
    <div className={'flex flex-col gap-0.5'}>
        <span className={'text-sm text-foreground/60'}>{label}</span>
        <span className={'text-sm text-foreground'}>{children}</span>
    </div>
);

const CustomerAccountDetailPage: React.FC = () => {
    const { accountId } = useParams<{ accountId: string }>();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';

    // Skip until the tenant is restored — on a hard reload the route renders
    // before the tenant lands in Redux, and a request without X-Tenant races
    // the cache reset that follows (see OrganizationsTab).
    const tenant = useSelector(tenantSelector);
    const { data: account, isLoading, isError } = useGetAccountQuery(
        { id: accountId ?? '' },
        { skip: !accountId || !tenant },
    );

    // #2102 item 4. Its own request rather than part of the detail payload: it
    // is a list that can grow, and the header should render without waiting.
    const {
        data: issueReportsData,
        isLoading: issueReportsLoading,
        isError: issueReportsError,
    } = useGetAccountIssueReportsQuery(
        { id: accountId ?? '' },
        { skip: !accountId || !tenant },
    );
    const issueReports = issueReportsData?.items ?? [];
    // Kept apart on purpose — see `AccountIssueReportMatch`. One is a fact, the
    // other is a string somebody typed into a public form.
    const linkedReports = issueReports.filter((report) => report.linkedVia === 'booking');
    const emailMatchedReports = issueReports.filter((report) => report.linkedVia === 'email');

    const issueReportRow = (report: AccountIssueReport) => (
        <li key={report.id}>
            {/* Opens the report itself. Support's next question after "did they
                report it?" is always "what did they say?", and the answer lived
                two screens away with no route between them. */}
            <button
                type={'button'}
                onClick={() => navigate(makeIssueReportDetailsUrl(report.id))}
                className={'w-full text-left text-sm text-foreground/80 hover:text-foreground '
                    + 'hover:underline transition-colors'}
            >
                <span className={'text-foreground/60'}>
                    {report.createdAt ? formatDate(report.createdAt, language) : naLabel}
                </span>
                {` · ${report.issueType || report.message || naLabel}`}
                {report.bookingNumber ? ` · ${report.bookingNumber}` : ''}
            </button>
        </li>
    );

    const naLabel = t('common.not_applicable');

    const fullNameOf = (row: AccountSummary): string => {
        const name = [row.firstName, row.lastName]
            .map((part) => part?.trim() ?? '')
            .filter((part) => part !== '')
            .join(' ');
        return name || naLabel;
    };

    const statusLabelOf = (row: AccountSummary): string =>
        row.status
            ? t(`customers.individuals.status.${row.status.toLowerCase()}`, {
                defaultValue: row.status,
            })
            : naLabel;

    if (isLoading || !tenant) {
        return (
            <div className={'p-6 flex justify-center py-16'}>
                <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
            </div>
        );
    }

    if (isError || !account) {
        return (
            <div className={'p-6 flex flex-col gap-4'}>
                <p className={'text-sm text-foreground/60'}>
                    {t('customers.account_detail.load_error')}
                </p>
                <div>
                    <CustomButton
                        title={t('customers.account_detail.back')}
                        onClick={() => navigate(paths.customers)}
                        isFilled={false}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={'p-6 flex flex-col gap-6'}>
            <PageHeader>
                <PageHeadline>
                    {fullNameOf(account)}
                    <span className={'block text-sm font-normal text-foreground/60 mt-0.5'}>
                        {account.email}
                    </span>
                </PageHeadline>
            </PageHeader>

            <button
                type={'button'}
                onClick={() => navigate(paths.customers)}
                className={'flex items-center gap-1.5 self-start text-sm text-foreground/60 '
                    + 'hover:text-foreground transition-colors'}
            >
                <ArrowLeft className={'w-4 h-4'} />
                {t('customers.account_detail.back')}
            </button>

            <div className={'bg-text-contrast rounded-[5px] shadow-sm px-6 py-5 flex flex-col gap-4'}>
                <h2 className={'text-base font-semibold text-foreground'}>
                    {t('customers.account_detail.sections.profile')}
                </h2>
                <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                    <Field label={t('customers.individuals.columns.name')}>
                        {fullNameOf(account)}
                    </Field>
                    <Field label={t('customers.individuals.columns.email')}>
                        {account.email}
                    </Field>
                    <Field label={t('customers.individuals.columns.phone')}>
                        {account.phone?.trim() || naLabel}
                    </Field>
                    <Field label={t('customers.individuals.columns.created')}>
                        {formatDate(account.createdAt, language)}
                    </Field>
                    <Field label={t('customers.individuals.columns.status')}>
                        {statusLabelOf(account)}
                    </Field>
                    {/* #2102 item 7. Three facts the account already carried
                        and no screen showed: whether the address was ever
                        confirmed, when they were last here, and what they
                        agreed to. The last is a consent record — without it,
                        a customer asking "what did I sign up for?" could not
                        be answered from the admin UI at all. */}
                    <Field label={t('customers.account_detail.email_verified')}>
                        {account.emailVerifiedAt
                            ? formatDate(account.emailVerifiedAt, language)
                            : t('customers.account_detail.email_not_verified')}
                    </Field>
                    <Field label={t('customers.account_detail.last_login')}>
                        {account.lastLoginAt
                            ? formatDate(account.lastLoginAt, language)
                            : t('customers.account_detail.never_logged_in')}
                    </Field>
                    <Field label={t('customers.account_detail.newsletter_consent')}>
                        {account.newsletterConsent
                            ? (account.newsletterConsentAt
                                ? formatDate(account.newsletterConsentAt, language)
                                : t('common.yes'))
                            : t('common.no')}
                    </Field>
                </div>
            </div>

            {/* #2102 item 7 — the two customer tabs were separate worlds: from
                a person you could not see that they belong to an organization,
                and #1926 made exactly that the thing deciding whether their
                booking is covered. */}
            <div className={'bg-text-contrast rounded-[5px] shadow-sm px-6 py-5 flex flex-col gap-3'}>
                <h2 className={'text-base font-semibold text-foreground'}>
                    {t('customers.account_detail.sections.memberships')}
                </h2>
                {account.memberships?.length ? (
                    <ul className={'flex flex-col gap-2'}>
                        {account.memberships.map((membership) => (
                            <li
                                key={membership.organizationId}
                                className={'text-sm text-foreground/80'}
                            >
                                <button
                                    type={'button'}
                                    className={'text-primary hover:underline'}
                                    onClick={() => navigate(
                                        makeOrganizationDetailUrl(membership.organizationId),
                                    )}
                                >
                                    {membership.organizationName}
                                </button>
                                {membership.unitName ? ` · ${membership.unitName}` : ''}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className={'text-sm text-foreground/60'}>
                        {t('customers.account_detail.no_memberships')}
                    </p>
                )}
            </div>

            <div className={'bg-text-contrast rounded-[5px] shadow-sm px-6 py-5 flex flex-col gap-3'}>
                <h2 className={'text-base font-semibold text-foreground'}>
                    {t('customers.account_detail.sections.activity')}
                </h2>
                <p className={'text-sm text-foreground/60'}>
                    {t('customers.account_detail.bookings_hint')}
                </p>
                <div className={'self-start'}>
                    <CustomButton
                        title={t('customers.account_detail.view_bookings')}
                        onClick={() => navigate(makeBookingsSearchUrl(account.email))}
                        isFilled={false}
                    />
                </div>
            </div>

            {/* #2102 item 4. This list reads `damagereport.account_id` and
                nothing else. The two older readings — reports on this account's
                orders, or reports whose free-text creator e-mail matches —
                return DIFFERENT sets, so a screen built on either would quietly
                disagree with itself. Reports with no order kept NULL in the
                backfill and belong to nobody here, deliberately. */}
            <div className={'bg-text-contrast rounded-[5px] shadow-sm px-6 py-5 flex flex-col gap-3'}>
                <h2 className={'text-base font-semibold text-foreground'}>
                    {t('customers.account_detail.sections.issue_reports')}
                </h2>
                {issueReportsLoading ? (
                    <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>
                ) : issueReportsError ? (
                    <p className={'text-sm text-foreground/60'}>
                        {t('customers.account_detail.issue_reports_error')}
                    </p>
                ) : issueReports.length === 0 ? (
                    <p className={'text-sm text-foreground/60'}>
                        {t('customers.account_detail.no_issue_reports')}
                    </p>
                ) : (
                    <>
                        {linkedReports.length > 0 && (
                            <ul className={'flex flex-col gap-2'}>
                                {linkedReports.map(issueReportRow)}
                            </ul>
                        )}
                        {emailMatchedReports.length > 0 && (
                            <div className={'flex flex-col gap-2 border-t border-foreground/10 pt-3'}>
                                {/* Said once, above the group, rather than as a
                                    badge on every row: it is one caveat about
                                    the whole set, not a property of each. */}
                                <p className={'text-xs text-foreground/60'}>
                                    {t('customers.account_detail.email_matched_hint')}
                                </p>
                                <ul className={'flex flex-col gap-2'}>
                                    {emailMatchedReports.map(issueReportRow)}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CustomerAccountDetailPage;
